import { components, MenuListProps } from 'react-select';
import { ADD_OPTION, OptionType } from '..';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useBulkUpdateCustomMap } from 'hooks/api/customMap/useUpdateCustomMap';
import { ReactElement, useMemo, useRef } from 'react';
import { debounce } from '../../../util';
import { reorderCustomMaps } from 'store/slices/CustomMapSlice';
import React from 'react';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

type Props =
	MenuListProps<OptionType> & {
		items: OptionType[];
	}

export default function CustomMenuList(menuProps: Props) {
	const { items, ...props } = menuProps;
	const menuListProps = props as MenuListProps<OptionType>;
	const { uuid } = useSelector((rs: RootState) => rs.roofData.data);
	const { mutateAsync: updateMapSortOrder, } = useBulkUpdateCustomMap(uuid);
	const pendingSortChanges = useRef<{ id: number; sortOrder: number }[]>([]);
	const dispatch = useDispatch();

	const flushSortChanges = useMemo(
		() =>
			debounce(() => {
				if (!pendingSortChanges.current.length) return;

				updateMapSortOrder(pendingSortChanges.current);
				pendingSortChanges.current = [];
			}, 500),
		[updateMapSortOrder]
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over) return;
		if (active.id === ADD_OPTION.value || over.id === ADD_OPTION.value) return;
		if (active.id === over.id) return;

		const oldIndex = items.findIndex(i => i.value === active.id);
		const newIndex = items.findIndex(i => i.value === over.id);
		if (oldIndex === -1 || newIndex === -1) return;

		const updated = arrayMove(items, oldIndex, newIndex); // ← built-in helper, no manual splice needed

		const hasChanged = updated.some((item, idx) => item.value !== items[idx]?.value);
		if (!hasChanged) return;

		const payload = updated.map((item, idx) => ({ id: item.value, sortOrder: idx + 1 }));
		dispatch(reorderCustomMaps(payload));
		pendingSortChanges.current = payload;
		flushSortChanges();
	};

	const childrenArray = React.Children.toArray(menuListProps.children) as ReactElement[];

	// separate input (-1)
	const inputItem = childrenArray.find(
		(child) => child?.props?.value === -1
	);

	// remaining items
	const optionItems = childrenArray.filter(
		(child) => child?.props?.value !== -1
	);

	// reverse options only if menuPlacement is top
	const orderedOptions =
	menuListProps.selectProps.menuPlacement === 'top'
		? [...optionItems].reverse()
		: optionItems;

	// keep input always last
	const orderedChildren = [...orderedOptions, inputItem];

	// SortableContext needs the list of sortable IDs (excluding the add option)
	const sortableIds = items
		.filter(i => i.value !== ADD_OPTION.value)
		.map(i => i.value);

	const sensors = useSensors(
		useSensor(PointerSensor, { // PointerSensor → distance-based (mouse/stylus)
			activationConstraint: {
				distance: 8, // prevents accidental drags on click
			},
		}),
		useSensor(TouchSensor, { // TouchSensor → delay-based (finger on iOS/Android)  
			activationConstraint: {
				delay: 250, // ms hold before drag starts
				tolerance: 5, // px movement allowed during delay
			},
		})
	);

	return (
		<components.MenuList {...props}>
			<DndContext 
				sensors={sensors} 
				collisionDetection={closestCenter} 
				onDragEnd={handleDragEnd}
			>
				<SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
					{orderedChildren}
				</SortableContext>
			</DndContext>
		</components.MenuList>
	);
}
